// AGC: CapacityBar Component
// AGC: Horizontal fill-level indicator for silos and tanks.
// AGC: Toggle between "Used" and "Free" views.
// AGC: Color: green (0-70%), yellow (70-90%), red (90-100%)
// AGC: Free view inverts: green = lots of free space, red = almost full.

import React, { useState, useCallback } from 'react'
import { ArrowLeftRight } from 'lucide-react'

interface CapacityBarProps {
  currentLevel: number       // AGC: Fill level in canonical units
  capacity: number           // AGC: Total capacity in canonical units
  displayUnit?: string       // AGC: For label display (e.g., 'ton', 'L')
  showToggle?: boolean       // AGC: Show the used/free toggle icon
  showLabels?: boolean       // AGC: Show text labels
  defaultView?: 'used' | 'free'
  className?: string
  compact?: boolean          // AGC: Compact variant for cards
}

function getColor(percent: number, view: 'used' | 'free'): string {
  // AGC: Used view: low fill = green (good), high fill = red (full/alert)
  // AGC: Free view: high free = green (good), low free = red (almost full)
  const p = view === 'free' ? 100 - percent : percent
  if (p >= 90) return '#EF4444'   // AGC: Red — full / almost no free space
  if (p >= 70) return '#F59E0B'   // AGC: Amber — warning
  return '#22C55E'                // AGC: Green — ok
}

function formatValue(canonical: number, displayUnit?: string): string {
  if (!displayUnit) return canonical.toFixed(1)
  // AGC: Simple frontend conversion matching UNIT_FACTORS in useWarehouse.ts
  const factors: Record<string, number> = {
    ton: 1000, t: 1000, sacas: 60, saca: 60,
    g: 0.001, gal: 3.785, gallon: 3.785, mL: 0.001, ml: 0.001,
    kg: 1, L: 1, l: 1,
  }
  const factor = factors[displayUnit] || 1
  const display = canonical / factor
  return `${display.toFixed(1)} ${displayUnit}`
}

export const CapacityBar: React.FC<CapacityBarProps> = ({
  currentLevel,
  capacity,
  displayUnit,
  showToggle = true,
  showLabels = true,
  defaultView = 'used',
  className = '',
  compact = false,
}) => {
  const [view, setView] = useState<'used' | 'free'>(defaultView)

  const percent = capacity > 0 ? Math.min(100, Math.max(0, (currentLevel / capacity) * 100)) : 0
  const barColor = getColor(percent, view)

  // AGC: In free view, the filled portion represents FREE space
  const barFillPercent = view === 'free' ? 100 - percent : percent

  const toggleView = useCallback(() => setView(v => v === 'used' ? 'free' : 'used'), [])

  const usedLabel = formatValue(currentLevel, displayUnit)
  const capacityLabel = formatValue(capacity, displayUnit)
  const freeLabel = formatValue(capacity - currentLevel, displayUnit)

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {/* AGC: Label row */}
      {showLabels && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span className="font-medium" style={{ color: barColor }}>
            {view === 'used' ? `${percent.toFixed(0)}% used` : `${(100 - percent).toFixed(0)}% free`}
          </span>
          <span>
            {view === 'used' ? `${usedLabel} / ${capacityLabel}` : `${freeLabel} free`}
          </span>
        </div>
      )}

      {/* AGC: Bar track + fill */}
      <div className="flex items-center gap-2">
        <div
          className={`flex-1 rounded-full bg-gray-200 overflow-hidden ${compact ? 'h-2' : 'h-3'}`}
          title={view === 'used'
            ? `Used: ${usedLabel} of ${capacityLabel}`
            : `Free: ${freeLabel} of ${capacityLabel}`}
        >
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${barFillPercent}%`,
              backgroundColor: barColor,
            }}
          />
        </div>

        {/* AGC: Toggle button */}
        {showToggle && (
          <button
            onClick={toggleView}
            className="shrink-0 p-0.5 rounded text-gray-400 hover:text-gray-600 transition-colors"
            title={view === 'used' ? 'Switch to free view' : 'Switch to used view'}
          >
            <ArrowLeftRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* AGC: Compact inline percentage */}
      {compact && !showLabels && (
        <span className="text-xs font-medium" style={{ color: barColor }}>
          {view === 'used' ? `${percent.toFixed(0)}%` : `${(100 - percent).toFixed(0)}% free`}
        </span>
      )}
    </div>
  )
}

export default CapacityBar
